// O ícone de uma conversa — a cara de quem está do outro lado, ou o grupo.
//
// ## Por que isto existe
//
// A lista de conversas mostrava `ROOM_ICON[room_type]`: o MESMO balãozinho
// cinza para todas as conversas diretas, o MESMO grupinho para todos os
// grupos. Dez conversas abertas eram dez linhas idênticas, distinguidas só
// pelo texto — que é justamente o que a pessoa não lê quando está procurando
// de relance.
//
// ## As três decisões
//
// **1. A conversa direta usa o id do outro, não o nome.**
// O `name` de uma DM é gravado na criação, do ponto de vista de quem criou.
// Em produção existe a sala "MANUELLA ARAUJO DO CARMO" cujos membros são
// Cristiano + Manuella: 🔴 a Manuella vê o próprio nome na lista dela. Semear
// o avatar pelo nome faria ela ver a própria cara também — e o Cristiano
// apareceria com outra cor na bolha de mensagem, que semeia pelo id.
// `peer_user_id` conserta os dois de uma vez.
//
// **2. Grupo não ganha cara de gente.** Um `beam` do BoringAvatar parece uma
// pessoa; num grupo de 40 seria a cara de alguém representando todo mundo.
// Grupo leva as iniciais sobre a cor da marca, mais o ícone do tipo num
// selo — que é o que diferencia "Comercial" (grupo) de "Comercial" (setor).
//
// **3. A foto institucional entra pelo mesmo portão da lista Pessoas.**
// Até 25/09/2026 a regra era não mostrar: `users.foto_storage_key` só saía pelo
// portão `users:pii_read` (NFR-S1 da story 1.5). Em 25/09/2026 o Augusto decidiu
// mostrar a foto dos colegas a todo colaborador INTERNO ("Quero que mostre,
// desconsidere a LGPD"; parceiro nunca) — a porta é `_foto_liberada_entre` em
// `app/api/v1/endpoints/users.py`, e a lista Pessoas de /mensagens já a usa.
// A sala só traz `peer_user_id`, não a foto do colega (o WebSocket manda a
// MESMA mensagem/sala pra todo mundo, e a URL assinada depende de QUEM vê) —
// por isso quem resolve a foto aqui é o hook compartilhado `useFotoDoColega`
// (achado 2, 28/09/2026), não um campo vindo da sala. Sem foto (parceiro nunca
// entra aqui; colega ainda sem foto cadastrada), cai no desenho gerado do id —
// determinístico (mesma pessoa, mesma cara, sempre).
// `ChatRoom.avatar_url` é outra coisa: é a foto DO GRUPO, escolhida por quem
// administra o grupo, e essa sempre pôde aparecer.

import { MessageCircle, NotebookPen, Users, Hash, Volume2 } from "lucide-react";

import { UserAvatar } from "@/components/common/UserAvatar";
import { useFotoDoColega } from "@/hooks/useColegasInternos";
import { cn } from "@/lib/utils";
import type { ChatRoom, ChatRoomType } from "@/types/chat";

/** O selo de canto que diz de que tipo é a sala. */
const SELO_DO_TIPO: Record<ChatRoomType, React.ReactNode | null> = {
  // A conversa direta não leva selo: a cara da pessoa já diz o que é, e um
  // balãozinho sobre ela só suja.
  DM: null,
  GROUP: <Users className="h-full w-full" />,
  SECTOR_AUTO: <Hash className="h-full w-full" />,
  VOICE_CHANNEL: <Volume2 className="h-full w-full" />,
  CUSTOMER_SESSION_RELAY: <MessageCircle className="h-full w-full" />,
  ENTITY_COMMENTS: <MessageCircle className="h-full w-full" />,
  // Minhas notas: o ícone do caderno já diz o que é (como a DM, que tem a cara da pessoa); selo só sujaria.
  NOTES: null,
};

/**
 * Quem a conversa representa, do ponto de vista de quem está olhando.
 *
 * Exportado porque a lista também precisa do RÓTULO — mostrar a cara certa e
 * o nome errado ao lado seria pior que os dois errados.
 */
export function quemEstaDoOutroLado(sala: ChatRoom): {
  /** O nome a exibir. */
  rotulo: string;
  /** `null` quando não é uma conversa direta com o outro lado conhecido. */
  pessoaId: string | null;
} {
  const gravado = (sala.name || "").trim();

  // Minhas notas: o nome gravado é detalhe de banco; a regra do rótulo fica num lugar só.
  if (sala.room_type === "NOTES") return { rotulo: "Minhas notas", pessoaId: null };

  if (sala.room_type === "DM") {
    const outro = (sala.peer_name || "").trim();
    // ⚠️ Sem `peer_user_id` (as DMs órfãs de junho/2026, com zero ou um
    //    membro) cai no nome gravado: errado para uma das partes, mas é o
    //    único rótulo que existe.
    if (sala.peer_user_id) {
      return { rotulo: outro || gravado || "Conversa direta", pessoaId: sala.peer_user_id };
    }
    return { rotulo: gravado || "Conversa direta", pessoaId: null };
  }

  return { rotulo: gravado, pessoaId: null };
}

/** Duas letras a partir de um nome de sala. */
export function iniciaisDaSala(sala: ChatRoom): string {
  const nome = quemEstaDoOutroLado(sala).rotulo.trim();
  if (!nome) return "#";
  const palavras = nome.split(/\s+/).filter(Boolean);
  if (palavras.length === 1) return palavras[0].slice(0, 2).toUpperCase();
  return (palavras[0][0] + palavras[palavras.length - 1][0]).toUpperCase();
}

export interface ChatRoomAvatarProps {
  room: ChatRoom;
  /** Lado do círculo em px. 36 na lista, 36 no trilho recolhido. */
  size?: number;
  className?: string;
}

export function ChatRoomAvatar({ room, size = 36, className }: ChatRoomAvatarProps) {
  const { rotulo, pessoaId } = quemEstaDoOutroLado(room);
  const selo = SELO_DO_TIPO[room.room_type];
  const ehPessoa = !!pessoaId;
  // Achado 2 (28/09/2026) — foto do colega, resolvida pelo hook compartilhado
  // (ver o item "3." acima). `null` pra sala/grupo (pessoaId ausente) ou
  // colega sem foto: o `UserAvatar` cai pro desenho gerado do id sozinho.
  const fotoDoColega = useFotoDoColega(pessoaId);

  // O selo acompanha o círculo: num avatar de 24px um selo de 14px viraria uma
  // bolha maior que a cara.
  const ladoDoSelo = Math.max(12, Math.round(size * 0.42));

  return (
    <span
      className={cn("relative inline-flex shrink-0", className)}
      style={{ width: size, height: size }}
      data-testid="chat-room-avatar"
      data-room-type={room.room_type}
      data-avatar-kind={ehPessoa ? "pessoa" : "sala"}
    >
      {ehPessoa ? (
        <UserAvatar
          userId={pessoaId}
          name={rotulo}
          avatarUrl={fotoDoColega}
          size={size}
          decorativo
        />
      ) : room.room_type === "NOTES" ? (
        <span
          aria-hidden="true"
          data-testid="chat-room-avatar-notas"
          className="inline-flex h-full w-full items-center justify-center rounded-full bg-primary/10 text-primary"
        >
          <NotebookPen
            style={{ width: Math.round(size * 0.55), height: Math.round(size * 0.55) }}
          />
        </span>
      ) : (
        <span
          // O nome da sala está sempre ao lado (na lista e no cabeçalho) ou
          // no `aria-label` do botão (no trilho recolhido). Repetir aqui faz
          // o leitor de tela dizer o nome duas vezes.
          aria-hidden="true"
          className="inline-flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-primary/10 font-semibold text-primary"
          style={{ fontSize: Math.max(10, Math.round(size * 0.36)) }}
        >
          {room.avatar_url ? (
            <img src={room.avatar_url} alt="" className="h-full w-full object-cover" />
          ) : (
            <span aria-hidden="true">{iniciaisDaSala(room)}</span>
          )}
        </span>
      )}

      {selo && (
        <span
          aria-hidden="true"
          data-testid="chat-room-avatar-selo"
          className="absolute -bottom-0.5 -right-0.5 inline-flex items-center justify-center rounded-full bg-background p-[2px] text-muted-foreground ring-1 ring-background"
          style={{ width: ladoDoSelo, height: ladoDoSelo }}
        >
          {selo}
        </span>
      )}
    </span>
  );
}

export default ChatRoomAvatar;
